import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Activity, Stamp, Zap, Download, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import UpgradePlanModal from '../components/UpgradePlanModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ActivityPage = () => {
  const { user } = useAuth();
  const [activity, setActivity] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [activityRes, campaignsRes] = await Promise.all([
        axios.get(`${API}/activity?limit=100`),
        axios.get(`${API}/campaigns`)
      ]);
      setActivity(activityRes.data);
      setCampaigns(campaignsRes.data);
    } catch (error) {
      console.error('Error fetching activity:', error);
      toast.error('Error al cargar actividad');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (user?.plan === 'free') {
      setUpgradeModalOpen(true);
      return;
    }
    try {
      const response = await axios.get(`${API}/export/stamps`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'stamps_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exportado');
    } catch (error) {
      if (error.response?.status === 403) {
        setUpgradeModalOpen(true);
      } else {
        toast.error(error.response?.data?.detail || 'Error al exportar');
      }
    }
  };

  const getCampaignName = (campaignId) => {
    const campaign = campaigns.find(c => c.id === campaignId);
    return campaign?.name || 'Campaña';
  };

  // Group activity by date
  const groupedActivity = activity.reduce((groups, event) => {
    const date = new Date(event.created_at).toLocaleDateString('es-ES', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(event);
    return groups;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="activity-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Actividad</h1>
          <p className="text-slate-600">Historial de sellos registrados</p>
        </div>
        <Button 
          variant="outline"
          onClick={handleExport}
          data-testid="export-csv-btn"
        >
          <Download className="w-5 h-5 mr-2" /> 
          Exportar CSV
          {user?.plan === 'free' && <Badge variant="secondary" className="ml-2">Growth</Badge>}
        </Button>
      </div>

      {/* Activity Timeline */}
      {activity.length === 0 ? (
        <motion.div 
          className="card-dashboard text-center py-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Activity className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-xl font-bold text-slate-900 mb-2">Sin actividad todavía</h3>
          <p className="text-slate-600">Los sellos registrados aparecerán aquí</p>
        </motion.div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedActivity).map(([date, events], groupIndex) => (
            <motion.div
              key={date}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: groupIndex * 0.05 }}
            >
              <div className="flex items-center gap-3 mb-3">
                <Calendar className="w-5 h-5 text-slate-400" />
                <h3 className="font-semibold text-slate-700 capitalize">{date}</h3>
                <Badge variant="secondary">{events.length} eventos</Badge>
              </div>
              
              <div className="card-dashboard">
                <div className="divide-y divide-slate-100">
                  {events.map((event, i) => (
                    <div 
                      key={event.id || i}
                      className="flex items-center gap-4 py-3 first:pt-0 last:pb-0"
                      data-testid={`activity-event-${event.id}`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        event.double_stamp 
                          ? 'bg-amber-100 text-amber-600' 
                          : 'bg-primary/10 text-primary'
                      }`}>
                        {event.double_stamp ? <Zap className="w-5 h-5" /> : <Stamp className="w-5 h-5" />}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">
                            +{event.stamps_added} sello{event.stamps_added > 1 ? 's' : ''}
                          </p>
                          {event.double_stamp && (
                            <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                              Doble sello
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 truncate">
                          {getCampaignName(event.campaign_id)} • {event.phone_hash?.substring(0, 12)}...
                        </p>
                      </div>
                      
                      <span className="text-sm text-slate-400 flex-shrink-0">
                        {new Date(event.created_at).toLocaleTimeString('es-ES', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Upgrade Plan Modal */}
      <UpgradePlanModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        title="Exportación CSV"
        message="La exportación de datos a CSV está disponible en los planes Growth y Scale. Mejora tu plan para descargar el historial completo de tu actividad."
      />
    </div>
  );
};

export default ActivityPage;
